import {
  APPLICATION_TYPE,
  DOC_UPLOAD_STATUS_IDS,
  useApplication,
  useFileUpload,
  useGoogleAnalytics,
  useModals,
  useDocumentUploads,
  authenticateDocument,
  verifyEligibility,
} from "@ca-dmv-radv/data";
import {
  CAPTURING_PHOTOS_IDLE,
  PROCESSING_PHOTOS_ERROR,
  PROCESSING_PHOTOS_IDLE,
  PROCESSING_PHOTOS_PROCESSING,
  PROCESSING_PHOTOS_SUCCESS,
  PROCESSING_PHOTOS_UPLOADING,
  useABBYYCapture,
} from "@ca-dmv-radv/files";
import { Trans, useTranslation } from "@ca-dmv-radv/translation";
import { ABBYY_ERROR_STOP, DocumentUpload } from "@ca-dmv/core";
import { React, useCallback, useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";

import { useNavigate } from "react-router-dom";
import { useMounted } from "@ca-dmv-radv/utilities";
import { initializeAID } from "@ca-dmv-radv/files/src/useAIDCapture";
import AIDDocumentCapture from "../AIDDocumentCapture";
import {
  aidCaptureError,
  aidEnabledCatDocIds,
  isAbbyyFlow,
  isMDLMobile,
  isDocument,
  Documents,
} from "../helpers/constants";
import useTranslateDocumentName from "../helpers/useTranslateDocumentName";
import RequiredPhotos from "../RequiredPhotos";
import Upload from "../Upload";
import ErrorExplanation from "../Document/ErrorExplanation";
import errorStatus from "../Document/errorStatus";
import MobileVerificationModal from "../Document/MobileVerificationModal";
import ProcessingPhotosErrorModal from "../Document/ProcessingPhotosErrorModal";
import ProcessingPhotosModal from "../Document/ProcessingPhotosModal";
import ProcessingPhotosSuccessModal from "../Document/ProcessingPhotosSuccessModal";
import SelectDifferentDocument from "../Document/SelectDifferentDocument";
import TakePhotoButton from "../Document/TakePhotoButton";
import {
  isFailureStatus,
  isPendingStatus,
  isRetryStatus,
  isSuccessStatus,
  isPendingConfirmationStatus,
  isAIDCapture,
} from "../PresenceVerification/constants";
import BlinkDocumentScan from "../Document/BlinkDocumentScan";

import BarcodeScanner from "../Document/BarcodeScanner/components/BarcodeScanner";

// TODO: Refactor transactionId to be const
// eslint-disable-next-line import/no-mutable-exports
let transactionId;
let isLoadedOnce;

export default function Document({
  containerClass,
  documentUpload,
  onCancelDocument,
  onSelectDifferentDocument,
  selectDifferentDocumentLabel,
  confirmFields,
  setConfirmFields,
  showIdentityForm,
  setShowIdentityForm,
  selectListOfDocs,
  recaptchaRef,
}) {
  if (!documentUpload) {
    return null;
  }
  const { t } = useTranslation();
  const {
    uploadingDocumentId,
    setUploadingDocumentId,
    takingPhotoDocumentId,
    setTakingPhotoDocumentId,
    selectDifferentDocumentId,
    setSelectDifferentDocumentId,
  } = useModals(null);
  const { fetchDocumentUploads, setShouldPingDocumentUploads } =
    useDocumentUploads();
  const { setFiles, postUploads } = useFileUpload();
  const { fetchApplication, applicationType, documentTypes } = useApplication();
  const { setAnalyticsAction } = useGoogleAnalytics();
  const translatedDocumentName = useTranslateDocumentName(
    documentUpload,
    applicationType
  );

  const uploadDocumentFiles = useCallback(
    async (doc) => {
      setShouldPingDocumentUploads(false);
      setAnalyticsAction(
        `Uploading ${doc.category}${doc.doc ? ` ${doc.doc}` : ""}`
      );
      const uploadSuccess = await postUploads(doc);
      await Promise.all([fetchApplication(), fetchDocumentUploads()]);
      setShouldPingDocumentUploads(true);
      setFiles((currentFiles) => {
        const newFiles = { ...currentFiles };
        delete newFiles[doc.id];
        return newFiles;
      });
      return uploadSuccess;
    },
    [
      setShouldPingDocumentUploads,
      setAnalyticsAction,
      postUploads,
      fetchApplication,
      fetchDocumentUploads,
      setFiles,
    ]
  );

  const {
    requiredPhotos,
    setRequiredPhotos,
    processingPhotos,
    setProcessingPhotos,
    verificationRequired,
    setVerificationRequired,
    resetUseABBYState,
    setIsCapturing,
    mutliPageError,
    setMultipageError,
    aidCaptureErrorCode,
    setAidCaptureErrorCode,
  } = useABBYYCapture({ chosenDocument: documentUpload, uploadDocumentFiles });
  const { setPassportFormData, passportFormData, resetFormData } =
    useFileUpload();
  const documentErrorStatus = errorStatus({ documentUpload });
  const showSelectDifferentDocument =
    (![
      DOC_UPLOAD_STATUS_IDS.VERIFIED,
      DOC_UPLOAD_STATUS_IDS.VERIFYING,
      DOC_UPLOAD_STATUS_IDS.UNDER_REVIEW,
      DOC_UPLOAD_STATUS_IDS.FAILED,
      DOC_UPLOAD_STATUS_IDS.UPLOADING,
      DOC_UPLOAD_STATUS_IDS.PROCESSING,
    ].includes(documentUpload.statusId) ||
      documentErrorStatus === ABBYY_ERROR_STOP ||
      (documentUpload.ATD === "PUC" && !confirmFields)) &&
    documentUpload.categoryId !== 7 &&
    selectListOfDocs?.length > 1;

  const [takePhotoClicked, setTakePhotoClicked] = useState(false);
  const [extractedDetails, setExtractedDetails] = useState({});
  const [extractInProgress, setExtractInProgress] = useState(false);
  const [blinkReadyToScan, setBlinkReadyToScan] = useState(false);
  const blinkRef = useRef(null);
  const documentUploadRef = useRef(null);
  const takePhotoLabel =
    applicationType === APPLICATION_TYPE.CEC
      ? t("cec-scanDL-label", "Scan Driver's License")
      : t("screens-Dashboard-takePhotoLabel", "Take Photo");

  const [useDigitalBazaarBarcodeScanner, setUseDigitalBazaarBarcodeScanner] =
    useState(undefined);

  useEffect(() => {
    if (applicationType === APPLICATION_TYPE.CEC) {
      const blinkFlag = documentUpload?.blinkIdOnForCec;

      if (blinkFlag === undefined) {
        setUseDigitalBazaarBarcodeScanner(undefined);
        return;
      }

      setUseDigitalBazaarBarcodeScanner(blinkFlag === "false");
    }
  }, [documentUpload]);

  const [barcodeReadyToScan, setBarcodeReadyToScan] = useState(false);
  const [result] = useState(null);
  const abbyyFlow = isAbbyyFlow(documentUpload?.ATD);
  const navigate = useNavigate();
  const mounted = useMounted();

  const hasValidValidationData = () => {
    if (
      documentUpload?.requiredExpirationDate !== null &&
      // Here we assume only maxDays is required
      documentUpload?.requiredExpirationDate?.maxDays === undefined
    ) {
      return false;
    }
    return true;
  };

  const showUploadPhoto = () => {
    if (isMDLMobile(applicationType) && !isMobile) {
      return false;
    }

    if (!hasValidValidationData()) {
      return false;
    }

    return (
      !isMobile &&
      (documentUpload.statusId === DOC_UPLOAD_STATUS_IDS.PENDING ||
        documentErrorStatus === ABBYY_ERROR_STOP)
    );
  };

  const showTakePhoto = () => {
    if (isMDLMobile(applicationType) && !isMobile) {
      return false;
    }
    if (applicationType === APPLICATION_TYPE.CEC) {
      return processingPhotos !== PROCESSING_PHOTOS_PROCESSING;
    }
    return (
      documentUpload.statusId === DOC_UPLOAD_STATUS_IDS.PENDING ||
      ([1, 7, 8, 9, 11].includes(documentUpload.categoryId) &&
        (isRetryStatus(documentUpload?.ATD) ||
          (isPendingConfirmationStatus(documentUpload?.ATD) &&
            !confirmFields))) ||
      (documentUpload.categoryId === 6 &&
        (isRetryStatus(documentUpload?.MTF) ||
          isRetryStatus(documentUpload?.MTS))) ||
      documentErrorStatus === ABBYY_ERROR_STOP
    );
  };

  // Post authenticateDocument
  const postAuthenticateDocument = async () => {
    setExtractInProgress(true);
    await authenticateDocument(documentUpload.id, applicationType).then(
      (response) => {
        transactionId = response.data.transactionId;
        setExtractedDetails(response.data.documentDetails);
        if (isPendingConfirmationStatus(response.data.decisionCode)) {
          if (
            isDocument(
              Documents.PASSPORT,
              documentTypes[documentUpload.catDocId]
            ) ||
            isDocument(
              Documents.PASSPORT_CARD,
              documentTypes[documentUpload.catDocId]
            )
          ) {
            setPassportFormData({
              ...passportFormData,
              Passport_Number: response.data.documentDetails?.documentNumber,
              Passport_Expiration_Date:
                response.data.documentDetails?.documentExpirationDate,
            });
          }
          setShowIdentityForm(true);
        } else if (
          isSuccessStatus(response.data.decisionCode) &&
          [
            APPLICATION_TYPE.VDL,
            APPLICATION_TYPE.RVD,
            APPLICATION_TYPE.RVO,
          ].includes(applicationType)
        ) {
          navigate("/final-confirmation");
        }
      }
    );
    await fetchDocumentUploads();
    setProcessingPhotos(PROCESSING_PHOTOS_IDLE);
    setTakingPhotoDocumentId(null);
    setExtractInProgress(false);
  };

  const showChangeImage = () => {
    if (isMDLMobile(applicationType) && !isMobile) {
      return false;
    }
    return (
      ![
        DOC_UPLOAD_STATUS_IDS.VERIFIED,
        DOC_UPLOAD_STATUS_IDS.VERIFYING,
        DOC_UPLOAD_STATUS_IDS.PROCESSING,
        DOC_UPLOAD_STATUS_IDS.UPLOADING,
        DOC_UPLOAD_STATUS_IDS.UNDER_REVIEW,
        DOC_UPLOAD_STATUS_IDS.FAILED,
      ].includes(documentUpload.statusId) &&
      applicationType !== APPLICATION_TYPE.CEC
    );
  };
  const showActiveCheck = [
    DOC_UPLOAD_STATUS_IDS.VERIFYING,
    DOC_UPLOAD_STATUS_IDS.UNDER_REVIEW,
    DOC_UPLOAD_STATUS_IDS.FAILED,
    DOC_UPLOAD_STATUS_IDS.PROCESSING,
    DOC_UPLOAD_STATUS_IDS.UPLOADING,
  ].includes(documentUpload.statusId);

  const showSuccessCheck =
    DOC_UPLOAD_STATUS_IDS.VERIFIED === documentUpload.statusId;
  const isSelectedDocumentID = documentUpload.id === takingPhotoDocumentId;

  let processingPhotosError;

  if (aidCaptureErrorCode) {
    processingPhotosError = t(
      `screen-aidCapture-errorCode${aidCaptureErrorCode}`,
      aidCaptureError[`errorCode${aidCaptureErrorCode}`]
    );
  } else if (mutliPageError) {
    processingPhotosError = t(
      "screens-Dashboard-mutliPageError",
      "Please provide required Front and Back photos of document."
    );
  } else {
    processingPhotosError = t(
      "screens-Dashboard-processingPhotoError",
      "We encountered an error while processing your photos"
    );
  }

  const countErrorCodes = () => {
    const errorCodes = documentUpload.validationData?.errorCodes || [];
    // remove duplicate codes
    return errorCodes.filter(
      (code, index) => errorCodes.indexOf(code) === index
    ).length;
  };

  const uploadPhotos = async () => {
    const uploadSuccess = await postUploads(documentUpload);
    if (uploadSuccess) {
      setProcessingPhotos(PROCESSING_PHOTOS_SUCCESS);
    } else {
      setProcessingPhotos(PROCESSING_PHOTOS_ERROR);
    }
  };

  useEffect(() => {
    if (
      isLoadedOnce ||
      !isMobile ||
      !mounted ||
      document.querySelector("#aid-script")
    ) {
      return;
    }

    isLoadedOnce = true;

    initializeAID();
  }, [isMobile, mounted]);

  useEffect(() => {
    if (
      processingPhotos === PROCESSING_PHOTOS_SUCCESS &&
      [1, 7, 8, 9, 11, 13].includes(documentUpload.categoryId)
    ) {
      if (
        !isAbbyyFlow(documentUpload.ATD) &&
        !isSuccessStatus(documentUpload.ATD)
      ) {
        postAuthenticateDocument();
        fetchDocumentUploads();
      }
    }
    if (processingPhotos === PROCESSING_PHOTOS_PROCESSING && abbyyFlow) {
      uploadPhotos();
    }
  }, [processingPhotos]);

  useEffect(() => {
    if ([1, 7, 8, 9, 11].includes(documentUpload.categoryId)) {
      if (
        isPendingStatus(documentUpload.ATD) &&
        Object.keys(extractedDetails).length !== 0
      ) {
        // setShowConfirmFields(true);
      } else if (isRetryStatus(documentUpload.ATD)) {
        setShowIdentityForm(false);
      } else if (isFailureStatus(documentUpload.ATD)) {
        // setAidFailure(true);
      }
    }
    fetchDocumentUploads();
  }, [documentUpload.ATD]);

  // For CEC-only to enable/disable Scan DL button
  useEffect(() => {
    if (applicationType !== APPLICATION_TYPE.CEC) return;

    const container = documentUploadRef.current;
    if (!container) return;

    const buttons = Array.from(
      container.querySelectorAll('button[type="button"]')
    );
    if (!buttons.length) return;

    const scanDLButton = buttons.find((btn) =>
      btn.textContent?.trim().includes(takePhotoLabel)
    );
    if (!scanDLButton) return;

    // Disable when:
    // 1. Flag is undefined (not loaded yet)
    // 2. BlinkID is the provider AND blinkReadyToScan is false
    if (
      useDigitalBazaarBarcodeScanner === undefined ||
      (useDigitalBazaarBarcodeScanner === false && !blinkReadyToScan)
    ) {
      scanDLButton.disabled = true;
      return;
    }

    // Otherwise enable
    scanDLButton.disabled = false;
  }, [useDigitalBazaarBarcodeScanner, blinkReadyToScan]);

  const onTakePhotoClick = () => {
    if (applicationType === APPLICATION_TYPE.CEC) {
      if (useDigitalBazaarBarcodeScanner === true) {
        setBarcodeReadyToScan(true);
      } else if (useDigitalBazaarBarcodeScanner === false) {
        blinkRef.current.startCameraScan();
      }
    } else {
      setTakePhotoClicked(true);
      setTakingPhotoDocumentId(documentUpload.id);
      resetUseABBYState();
      resetFormData();
      if ([1, 7, 8, 9, 11, 13].includes(documentUpload.categoryId)) {
        if (
          isDocument(
            Documents.BIRTH_CERTIFICATE,
            documentTypes[documentUpload.catDocId]
          ) ||
          isDocument(
            Documents.PASSPORT,
            documentTypes[documentUpload.catDocId]
          ) ||
          isDocument(
            Documents.PASSPORT_CARD,
            documentTypes[documentUpload.catDocId]
          )
        ) {
          setVerificationRequired("required");
        }
        if (
          isAIDCapture(documentUpload.ATD) &&
          aidEnabledCatDocIds.includes(documentUpload.catDocId)
        ) {
          setRequiredPhotos("required");
        } else if (
          abbyyFlow &&
          aidEnabledCatDocIds.includes(documentUpload.catDocId)
        ) {
          if (
            isDocument(
              Documents.PASSPORT,
              documentTypes[documentUpload.catDocId]
            )
          ) {
            setShowIdentityForm(true);
          } else if (
            isDocument(
              Documents.PASSPORT_CARD,
              documentTypes[documentUpload.catDocId]
            )
          ) {
            setRequiredPhotos("required");
          }
        }
      }
    }
  };

  const processingErrorModalClose = () => {
    if (mutliPageError) {
      if (
        isDocument(
          Documents.PASSPORT_CARD,
          documentTypes[documentUpload.catDocId]
        )
      ) {
        resetFormData();
      }
      setRequiredPhotos("required");
      setMultipageError(false);
    } else if (aidCaptureErrorCode) {
      setAidCaptureErrorCode(null);
    }
    setProcessingPhotos(PROCESSING_PHOTOS_IDLE);
    setTakingPhotoDocumentId(null);
  };

  const submitAIDCapture = () => {
    // setCloseModal(true)
    setProcessingPhotos(PROCESSING_PHOTOS_PROCESSING);
    setVerificationRequired("submitPhotos");
    if (applicationType === APPLICATION_TYPE.CEC) {
      setProcessingPhotos(PROCESSING_PHOTOS_SUCCESS);
    } else {
      uploadPhotos();
    }
  };

  const determineShouldShowMobileVerificationModal = () => {
    const requiredPhotosCompleted = [null, "accepted"].includes(requiredPhotos);
    if (
      isMobile &&
      ((verificationRequired === "required" &&
        requiredPhotosCompleted &&
        takePhotoClicked &&
        isSelectedDocumentID) ||
        showIdentityForm)
    ) {
      return true;
    }
    return false;
  };

  const shouldShowMobileVerificationModal =
    determineShouldShowMobileVerificationModal();

  const determineShouldShowRequiredPhotos = () => {
    if (
      isMobile &&
      requiredPhotos === "required" &&
      takePhotoClicked &&
      isSelectedDocumentID
    ) {
      if (isAbbyyFlow(documentUpload.ATD)) {
        return true;
      }
    }
    return false;
  };

  const shouldShowRequiredPhotos = determineShouldShowRequiredPhotos();

  const formatDate = (month, day, year) =>
    `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  const supportedFormats = ["pdf417"];

  const restart = useCallback(() => {
    setBarcodeReadyToScan(false);
  }, []);

  // Called when a barcode is successfully scanned
  const handleScan = useCallback((scanResult) => {
    setBarcodeReadyToScan(false);

    setProcessingPhotos(PROCESSING_PHOTOS_PROCESSING);

    (async () => {
      const recaptchaToken = await recaptchaRef.current.executeAsync();
      recaptchaRef.current.reset();

      const dlNumber = scanResult.text.match(/DAQ([^\n\r]+)/)?.[1];
      const lastName = scanResult.text.match(/DCS([^\n\r]+)/)?.[1];
      const birthDate = scanResult.text.match(/DBB(\d{8})/)?.[1];

      const month = birthDate ? parseInt(birthDate.slice(4, 6), 10) : null;
      const day = birthDate ? parseInt(birthDate.slice(6, 8), 10) : null;
      const year = birthDate ? parseInt(birthDate.slice(0, 4), 10) : null;

      const formattedDate = formatDate(month, day, year);
      const driversLicenseObj = {
        dlNumber,
        lastName,
        dob: formattedDate,
      };

      const payload = {
        ...driversLicenseObj,
        recaptchaToken,
        source: "BarcodeScanner",
      };

      await verifyEligibility(payload).then((response) => {
        navigate("/result", { state: { isEligible: response.success } });
      });
    })();
  }, []);

  return (
    <>
      <div ref={documentUploadRef}>
        <DocumentUpload
          legend={translatedDocumentName || documentUpload.name}
          containerClass={containerClass}
          showSelectDifferentDocument={
            showSelectDifferentDocument && !documentUpload.parentId
          }
          showUploadPhoto={showUploadPhoto()}
          showTakePhoto={showTakePhoto()}
          uploadImageLabel={t("screens-Dashboard-uploadPhoto", "Upload Photo")}
          takePhotoLabel={takePhotoLabel}
          selectDifferentDocumentLabel={
            selectDifferentDocumentLabel ||
            t("screens-Dashboard-changeLabel", "Select Different Document")
          }
          orLabel={t("screens-Dashboard-orLabel", "or")}
          onUploadClick={() => {
            resetFormData();
            setUploadingDocumentId(documentUpload.id);
          }}
          onTakePhotoClick={onTakePhotoClick}
          onSelectDifferentDocument={() => {
            if (onSelectDifferentDocument) {
              onSelectDifferentDocument();
            } else {
              setSelectDifferentDocumentId(documentUpload.id);
            }
          }}
          errorStatus={documentErrorStatus}
          errorTitle={
            documentErrorStatus === ABBYY_ERROR_STOP ? (
              <Trans
                i18nKey="screens-Dashboard-abbyyErrorTitle"
                defaults="There was an error in your submission. Please correct the following {{items}} or select a different document."
                values={{
                  items: countErrorCodes() > 1 ? "items" : "item",
                }}
              />
            ) : (
              ""
            )
          }
          errorExplanation={
            <ErrorExplanation documentUpload={documentUpload} />
          }
          showChangeImage={showChangeImage()}
          onChangeImage={() => {
            setUploadingDocumentId(documentUpload.id);
          }}
          showActiveCheck={showActiveCheck}
          showSuccessCheck={showSuccessCheck}
        />
      </div>
      {documentUpload.id === uploadingDocumentId && (
        <Upload
          onClose={() => {
            setUploadingDocumentId(null);
          }}
          documentUpload={documentUpload}
          modalTitle={
            isDocument(
              Documents.SIGNATURE_IMAGE,
              documentTypes[documentUpload.catDocId]
            )
              ? t(
                  "nch-screens-category10-howTo",
                  "How to Provide Your Signature"
                )
              : `${t("shared.uploadLabel", "Upload")} ${
                  translatedDocumentName || documentUpload.name
                }`
          }
          uploadDocumentFiles={uploadDocumentFiles}
        />
      )}
      {isSelectedDocumentID && !isMobile && (
        <TakePhotoButton documentUpload={documentUpload} />
      )}
      {/* Digital Bazaar Scanner
      
        - Only show if mobile, CEC application, and backend flag is set to NOT use BlinkID
        - On mount, Digital Bazaar Scanner will load the camera. To avoid unnecessarily loading the camera, 
        we only render the component (and thus load the camera) when the user clicks the "Take Photo" button. 
        This means that if the user never clicks "Take Photo", we never load the camera.

      */}
      {isMobile &&
        applicationType === APPLICATION_TYPE.CEC &&
        useDigitalBazaarBarcodeScanner === true &&
        barcodeReadyToScan &&
        !result && (
          <BarcodeScanner
            tipText="Scan the back of your Driver's License"
            formatsToSupport={supportedFormats}
            onClose={restart}
            onResult={handleScan}
          />
        )}
      {/* BlinkID Scanner
      
        - Only show if mobile, CEC application, and backend flag is set to use BlinkID
        - Different to how Digital Bazaar Scanner is implemented, we always render the BlinkID component if flag is set, 
        but we don't start the camera until the user clicks the "Take Photo" button. This is because the BlinkID component needs 
        to be rendered in order to start the camera, so we can't conditionally render it like we do with the Digital Bazaar Scanner. 
        Instead, we control when the camera starts through a ref that is triggered by the "Take Photo" button.

      */}
      {isMobile &&
        applicationType === APPLICATION_TYPE.CEC &&
        useDigitalBazaarBarcodeScanner === false && (
          <BlinkDocumentScan
            blinkRef={blinkRef}
            recaptchaRef={recaptchaRef}
            setProcessingPhotos={setProcessingPhotos}
            setBlinkReadyToScan={setBlinkReadyToScan}
          />
        )}
      {isSelectedDocumentID &&
        requiredPhotos === "required" &&
        processingPhotos === PROCESSING_PHOTOS_IDLE &&
        isMobile &&
        isAIDCapture(documentUpload.ATD) &&
        applicationType !== APPLICATION_TYPE.CEC && (
          <AIDDocumentCapture
            documentName={documentUpload.name}
            onClose={() => {
              setRequiredPhotos("canceled");
              setTakingPhotoDocumentId(null);
            }}
            onCapture={() => {
              setVerificationRequired("accepted");
              setIsCapturing(CAPTURING_PHOTOS_IDLE);
            }}
            onConfirm={submitAIDCapture}
            requiredPages={documentUpload.requiredPages}
            document={documentUpload}
          />
        )}
      {shouldShowRequiredPhotos && (
        <RequiredPhotos
          documentName={documentUpload.name}
          onClose={() => {
            setRequiredPhotos("canceled");
            setTakingPhotoDocumentId(null);
            setTakePhotoClicked(false);
          }}
          onCapture={() => {
            if (
              isDocument(
                Documents.PASSPORT_CARD,
                documentTypes[documentUpload.catDocId]
              ) ||
              documentUpload.requiredExpirationDate
            ) {
              setShowIdentityForm(true);
            } else {
              setVerificationRequired("accepted");
            }
            setRequiredPhotos("accepted");
          }}
          onConfirm={() => {
            setVerificationRequired("submitPhotos");
          }}
          requiredPages={documentUpload.requiredPages}
          document={documentUpload}
        />
      )}
      {shouldShowMobileVerificationModal && (
        <MobileVerificationModal
          t={t}
          documentUpload={documentUpload}
          setVerificationRequired={setVerificationRequired}
          setShowIdentityForm={setShowIdentityForm}
          extractedDetails={extractedDetails}
          setConfirmFields={setConfirmFields}
          extractInProgress={extractInProgress}
          setExtractInProgress={setExtractInProgress}
          setRequiredPhotos={setRequiredPhotos}
          applicationType={applicationType}
          onClose={() => {
            if (documentUpload?.requiredPages) {
              setRequiredPhotos("required");
            }
            setTakingPhotoDocumentId(null);
            setShowIdentityForm(false);
            setTakePhotoClicked(false);
            // setIsCapturing(CAPTURING_PHOTOS_IDLE);
          }}
        />
      )}
      {(((isSelectedDocumentID || applicationType === APPLICATION_TYPE.CEC) &&
        [PROCESSING_PHOTOS_UPLOADING, PROCESSING_PHOTOS_PROCESSING].includes(
          processingPhotos
        )) ||
        extractInProgress) &&
        isMobile && <ProcessingPhotosModal t={t} />}
      {isSelectedDocumentID &&
        processingPhotos === PROCESSING_PHOTOS_SUCCESS &&
        isMobile &&
        abbyyFlow && (
          <ProcessingPhotosSuccessModal
            onClose={() => {
              setProcessingPhotos(PROCESSING_PHOTOS_IDLE);
              setTakingPhotoDocumentId(null);
              fetchDocumentUploads();
            }}
            t={t}
          />
        )}
      {((isSelectedDocumentID &&
        processingPhotos === PROCESSING_PHOTOS_ERROR) ||
        mutliPageError ||
        aidCaptureErrorCode) &&
        isMobile && (
          <ProcessingPhotosErrorModal
            onClose={processingErrorModalClose}
            error={processingPhotosError}
            t={t}
          />
        )}
      {selectDifferentDocumentId === documentUpload.id && onCancelDocument && (
        <SelectDifferentDocument
          onConfirm={onCancelDocument}
          onClose={() => {
            setSelectDifferentDocumentId(null);
            setTakingPhotoDocumentId(null);
          }}
        />
      )}
    </>
  );
}

export { transactionId };
